import { AdvancedSelect } from '../../../widgets/select';
import { BackButton, PrimaryButton } from '../../../widgets/buttons';
import { Dialog, DialogWindow, useDialogManager } from '../../../widgets/dialog';
import { FormControl } from '../../../widgets/formControl';
import { SearchInput } from '../../../widgets/input';
import React, { FC, useContext, useTransition } from 'react';
import { graphql, useLazyLoadQuery, usePaginationFragment } from 'react-relay';
import { useNavigate } from 'react-router-dom';
import { Preview3DStoreContext } from 'src/components/widgets/preview3D';
import styled from 'styled-components';
import config from '../../../../shared/config';
import { ErrorMessage } from '../../../../shared/globals';
import { T } from '../../../../shared/helpers';
import NotFoundIcon from '../../../../shared/icons/icon-not-found.svg';
import { CatalogListContent, CatalogListLoader } from '../../../widgets/catalogList';
import { CustomErrorBoundary } from '../../../widgets/customErrorBoundary';
import { Section } from '../../../widgets/section';
import { damDialogPaginationQuery } from './__generated__/damDialogPaginationQuery.graphql';
import { damDialogQuery } from './__generated__/damDialogQuery.graphql';
import { damDialog_fragment$key } from './__generated__/damDialog_fragment.graphql';

const DialogDecorator = styled(DialogWindow)`
    width: 90%;
    height: 100%;
    flex-basis: unset;
`;

const Container = styled.div`
    height: 100%;
    width: 100%;
    background: white;
    position: relative;
`;

const LayoutContainer = styled.div`
    height: 100%;
    position: relative;
`;

const FormControlToolsContainers = styled.div`
    display: grid;
    grid-template-columns: 3fr 1fr;
    grid-gap: 20px;
    position: fixed;
    width: 73%;
    top: 12%;
    z-index: 10;
`;

interface DAMDialogProps {
    onClose: (productAssetUrl: string) => void;
    isMaterialReplacement?: boolean;
}

interface DAMAsset {
    id: string;
    title: string;
    thumbnailUrl: string;
    fileUrl: string;
}

const RequestOtherProductsButton = styled(PrimaryButton)`
    position: fixed;
    bottom: 6%;
    left: 40%;
`;

const damDialogFragment = graphql`
    fragment damDialog_fragment on Query
    @refetchable(queryName: "damDialogPaginationQuery")
    @argumentDefinitions(
        count: { type: "Int", defaultValue: 20 }
        cursor: { type: "String", defaultValue: "0" }
        title: { type: "String", defaultValue: "" }
        filterMasks: { type: "String", defaultValue: "" }
        sortBy: { type: "String", defaultValue: "" }
    ) {
        damScenes(first: $count, after: $cursor, title: $title, filterMasks: $filterMasks, sortBy: $sortBy) @connection(key: "damDialog_damScenes") {
            __id # This is the cache id
            edges {
                node {
                    ... on DamScene {
                        id
                        title
                        assets {
                            asset {
                                title
                                file {
                                    fileUrl
                                }
                                thumbnailUrl
                            }
                        }
                    }
                }
            }
        }
    }
`;

const DAMDialogProductsListContent: FC<{ handleSelectAsset: (asset: DAMAsset) => void }> = ({ handleSelectAsset }) => {
    const [text, setText] = React.useState('');
    const [isPending, startTransition] = useTransition();
    const [isFirstLoad, setIsFirstLoad] = React.useState(false);
    const [filters, setFilters] = React.useState<string>('created_at:desc');
    const navigate = useNavigate();
    const { closeDialog } = useDialogManager();

    const query = useLazyLoadQuery<damDialogQuery>(
        graphql`
            query damDialogQuery($filterMasks: String, $sortBy: String) {
                ...damDialog_fragment @arguments(filterMasks: $filterMasks, sortBy: $sortBy)
            }
        `,
        {
            filterMasks: 'published',
            sortBy: 'created_at:desc'
        },
        { fetchPolicy: 'network-only' }
    );

    const { data, loadNext, refetch, hasNext, isLoadingNext } = usePaginationFragment<damDialogPaginationQuery, damDialog_fragment$key>(
        damDialogFragment,
        query
    );

    if (!isFirstLoad) setIsFirstLoad(true);
    if (data && data.damScenes?.edges && data.damScenes?.edges?.length === 0 && !isFirstLoad) {
        closeDialog('dam-dialog');
        window.location.href = `/${config.culture}/Admin/DAM?page=/estimates`;
    } else {
        return (
            <LayoutContainer>
                <Section>
                    <FormControl>
                        <FormControlToolsContainers>
                            <SearchInput
                                fullWidth
                                placeholder={T._('Search assets...', 'Admin')}
                                onSearch={(filter: string) => {
                                    setText(filter);
                                    startTransition(() => {
                                        refetch(
                                            {
                                                title: filter,
                                                filterMasks: 'published' + (filter.trim() && filter.length > 0 ? ',title' : ''),
                                                sortBy: filters ? filters : ''
                                            },

                                            { fetchPolicy: 'network-only' }
                                        );
                                    });
                                }}
                            />
                            <AdvancedSelect
                                options={[
                                    { label: T._('A-Z', 'Admin'), value: 'title:asc' },
                                    { label: T._('Z-A', 'Admin'), value: 'title:desc' },
                                    { label: T._('most recent', 'Admin'), value: 'created_at:desc' },
                                    { label: T._('less recent', 'Admin'), value: 'created_at:asc' }
                                ]}
                                defaultValue={{ label: T._('most recent', 'Admin'), value: 'created_at:desc' } as any}
                                placeholder={T._('Sort by...', 'Admin')}
                                onChange={(value: { value: string }) => {
                                    setFilters(value?.value ?? '');
                                    startTransition(() => {
                                        refetch(
                                            {
                                                title: text,
                                                filterMasks: 'published' + (text.trim() && text.length > 0 ? ',title' : ''),
                                                sortBy: value ? value.value : ''
                                            },
                                            { fetchPolicy: 'network-only' }
                                        );
                                    });
                                }}
                            />
                        </FormControlToolsContainers>
                    </FormControl>
                    <div style={{ height: '40px' }}></div>
                    {isPending ? (
                        CatalogListLoader
                    ) : (
                        <CatalogListContent
                            noItemsIcon={<NotFoundIcon />}
                            noItemsMessage={T._('No asset was found', 'Admin')}
                            noItemsSubMessage={T._('Sorry, no asset was found for your search. Please try using different keywords.', 'Admin')}
                            models={
                                data.damScenes?.edges?.map((edge) => ({
                                    id: edge.node.id!.toString(),
                                    title: edge.node.title ?? 'N/D',
                                    subtitle: '',
                                    thumbnail: edge.node.assets?.[0]?.asset?.thumbnailUrl ?? '',
                                    selected: false
                                })) ?? []
                            }
                            tooltipHtml={'This asset is not compatible with the Visual Product Customizer'}
                            isDamProductsCatalog
                            loadNext={() => loadNext(20)}
                            hasNext={hasNext}
                            isLoadingNext={isLoadingNext}
                            onItemClick={(asset) => {
                                handleSelectAsset({
                                    id: asset.id,
                                    title: asset.title,
                                    thumbnailUrl: asset.thumbnail,
                                    fileUrl: data!.damScenes!.edges!.find((edge) => edge.node.id === asset.id)?.node.assets?.[0]?.asset?.file?.fileUrl || ''
                                });
                            }}
                        />
                    )}
                </Section>
                {/* {data && data.damScenes?.edges && data.damScenes?.edges?.length > 0 && (
					<RequestOtherProductsButton
						onClick={() => {
							closeDialog('dam-dialog');
							navigate(backofficeUrl('/DAM?page=/estimates'));
						}}
					>
						{T._('Request asset creation services', 'Admin')}
					</RequestOtherProductsButton>
				)} */}
            </LayoutContainer>
        );
    }
};

const DAMDialogProductAssetsListContent: FC<{
    asset: DAMAsset;
    onClose(assetUrl: string): void;
    onBackClick(): void;
}> = ({ asset, onClose, onBackClick }) => {
    const [isPending, startTransition] = useTransition();
    const [filter, setFilter] = React.useState<string>('');
    return (
        <LayoutContainer>
            <Section>
                <FormControl>
                    <FormControlToolsContainers>
                        <BackButton onClick={() => onBackClick()} />
                    </FormControlToolsContainers>
                </FormControl>
                <div style={{ height: '40px' }}></div>
                <CatalogListContent
                    models={[
                        {
                            id: asset.id,
                            title: asset.title,
                            subtitle: '',
                            thumbnail: asset.thumbnailUrl,
                            selected: true
                        }
                    ]}
                    loadNext={() => {}}
                    hasNext={false}
                    isLoadingNext={false}
                    onItemClick={(model) => onClose(asset.fileUrl)}
                />

                {isPending && CatalogListLoader}
            </Section>
        </LayoutContainer>
    );
};

const DAMDialog: FC<DAMDialogProps> = ({ onClose, isMaterialReplacement }) => {
    const [selectedAsset, setSelectedAsset] = React.useState<DAMAsset | null>(null);
    const preview3DStore = useContext(Preview3DStoreContext);
    console.log('dam dialog render');
    return (
        <Dialog title={T._('Select an asset from Asset Library', 'Admin')} windowDecorator={DialogDecorator}>
            <Container>
                <CustomErrorBoundary render={() => <ErrorMessage>{T._('Failed loading DAM assets.', 'Admin')}</ErrorMessage>}>
                    <Preview3DStoreContext.Provider value={preview3DStore}>
                        {!selectedAsset && <DAMDialogProductsListContent handleSelectAsset={setSelectedAsset} />}
                        {selectedAsset && (
                            <Preview3DStoreContext.Provider value={preview3DStore}>
                                <DAMDialogProductAssetsListContent onBackClick={() => setSelectedAsset(null)} onClose={onClose} asset={selectedAsset} />
                            </Preview3DStoreContext.Provider>
                        )}
                    </Preview3DStoreContext.Provider>
                </CustomErrorBoundary>
            </Container>
        </Dialog>
    );
};

export default DAMDialog;
